// El árbol de categorías, como lógica pura —
// ADR-2026-09-07-categorias-de-tienda.
//
// La base guarda una lista plana con `parent_id`, que es como lo guarda
// Tiendanube. La pantalla necesita otra cosa: una lista ORDENADA y con nivel,
// para dibujar la indentación y para que las hijas salgan abajo de su madre.
// Armar eso a mano en el componente es donde aparecen los recorridos infinitos.
//
// Todo lo de acá aguanta datos rotos a propósito. La base ya impide los ciclos
// con un trigger, pero el front recibe lo que le mandan: una madre borrada en
// otra pestaña deja hijas colgando, y una lista con un ciclo colgaría el
// navegador si el recorrido confiara.

export interface CategoriaPlana {
  id: string;
  nombre: string;
  parentId: string | null;
}

export interface CategoriaEnArbol<T extends CategoriaPlana = CategoriaPlana> {
  categoria: T;
  /** 0 = primer nivel. Es la indentación que dibuja la lista. */
  nivel: number;
  /** Para saber si mostrarle el triangulito de plegar. */
  tieneHijas: boolean;
}

/** Alfabético y sin distinguir mayúsculas ni acentos, que es como se busca. */
function porNombre(a: CategoriaPlana, b: CategoriaPlana): number {
  return a.nombre.localeCompare(b.nombre, "es", { sensitivity: "base" });
}

/**
 * La lista plana en orden de lectura: cada categoría seguida de su descendencia,
 * con el nivel para indentar.
 *
 * Una categoría cuya madre no está en la lista se trata como de primer nivel en
 * vez de desaparecer: si la madre se borró en otra pestaña, esconder a las hijas
 * las volvería invisibles pero no las borraría, y el usuario tendría categorías
 * fantasma publicándose sin poder verlas.
 */
export function aplanarArbol<T extends CategoriaPlana>(
  categorias: readonly T[],
  /** Ids plegados: su descendencia no se lista. */
  plegadas: ReadonlySet<string> = new Set(),
): Array<CategoriaEnArbol<T>> {
  const existe = new Set(categorias.map((c) => c.id));
  const hijasDe = new Map<string | null, T[]>();
  for (const c of categorias) {
    // La huérfana cuelga de la raíz.
    const madre = c.parentId && existe.has(c.parentId) ? c.parentId : null;
    const grupo = hijasDe.get(madre);
    if (grupo) grupo.push(c);
    else hijasDe.set(madre, [c]);
  }
  for (const grupo of hijasDe.values()) grupo.sort(porNombre);

  const out: Array<CategoriaEnArbol<T>> = [];
  // `vistas` corta cualquier ciclo que llegue desde la base: sin esto, A→B→A
  // recorrería para siempre.
  const vistas = new Set<string>();

  const bajar = (madre: string | null, nivel: number) => {
    for (const c of hijasDe.get(madre) ?? []) {
      if (vistas.has(c.id)) continue;
      vistas.add(c.id);
      const hijas = hijasDe.get(c.id) ?? [];
      out.push({ categoria: c, nivel, tieneHijas: hijas.length > 0 });
      if (!plegadas.has(c.id)) bajar(c.id, nivel + 1);
    }
  };
  bajar(null, 0);
  return out;
}

/** Una fila del árbol con lo que hace falta para dibujar sus líneas guía. */
export interface FilaConGuias<T extends CategoriaPlana = CategoriaPlana> extends CategoriaEnArbol<T> {
  /** Es la última de sus hermanas: su codo es └ y no ├. */
  esUltima: boolean;
  /**
   * Por cada nivel de 0 a `nivel - 1`: si la ancestra de ESE nivel todavía tiene hermanas más abajo,
   * su línea vertical sigue de largo por esta fila. `guias[k]` es la de la ancestra de nivel k.
   */
  guias: boolean[];
}

/**
 * `aplanarArbol` más las líneas guía (ADR-2026-10-22-el-arbol-de-categorias-con-lineas). Se
 * recorre de abajo hacia arriba llevando, por nivel, si ya apareció una hermana más abajo: una
 * fila menos profunda corta las líneas de los niveles de debajo.
 */
export function aplanarArbolConGuias<T extends CategoriaPlana>(
  categorias: readonly T[],
  plegadas: ReadonlySet<string> = new Set(),
): Array<FilaConGuias<T>> {
  const filas = aplanarArbol(categorias, plegadas);
  const hayMas: boolean[] = [];
  const out = new Array<FilaConGuias<T>>(filas.length);
  for (let i = filas.length - 1; i >= 0; i--) {
    const f = filas[i];
    hayMas.length = f.nivel + 1;
    out[i] = {
      ...f,
      esUltima: !hayMas[f.nivel],
      guias: Array.from({ length: f.nivel }, (_, k) => !!hayMas[k]),
    };
    hayMas[f.nivel] = true;
  }
  return out;
}

/**
 * Los ids de una categoría y toda su descendencia.
 *
 * Se usa para dos cosas: avisar cuántas se lleva puestas un borrado, y sacar de
 * los destinos posibles las que no pueden serlo.
 */
export function ramaDe(categorias: readonly CategoriaPlana[], id: string): Set<string> {
  const hijasDe = new Map<string, string[]>();
  for (const c of categorias) {
    if (!c.parentId) continue;
    const grupo = hijasDe.get(c.parentId);
    if (grupo) grupo.push(c.id);
    else hijasDe.set(c.parentId, [c.id]);
  }
  const rama = new Set<string>();
  const pendientes = [id];
  while (pendientes.length > 0) {
    const actual = pendientes.pop()!;
    if (rama.has(actual)) continue;
    rama.add(actual);
    pendientes.push(...(hijasDe.get(actual) ?? []));
  }
  return rama;
}

/**
 * A qué categorías se puede mover `id`.
 *
 * Quedan afuera ella misma y toda su descendencia: meter una categoría adentro
 * de su propia hija cierra un círculo, y un árbol con un círculo no tiene raíz
 * ni se puede dibujar. La base lo rechaza igual, pero un error del servidor
 * después de elegir es peor que no ofrecer la opción.
 */
export function destinosPosibles<T extends CategoriaPlana>(
  categorias: readonly T[],
  id: string,
): T[] {
  const prohibidas = ramaDe(categorias, id);
  return categorias.filter((c) => !prohibidas.has(c.id));
}

/** "Remeras › Oversize › Negras" — dónde está parada una categoría. */
export function caminoDe(categorias: readonly CategoriaPlana[], id: string): string {
  return partesDelCamino(categorias, id).join(" › ");
}

/**
 * El camino como LISTA, de la raíz a la hoja: `["Remeras", "Oversize"]`.
 *
 * `caminoDe` lo junta con "›" para mostrarlo; la exportación a carpetas necesita
 * las partes sueltas, porque cada una es una carpeta. Es la misma caminata, así
 * que vive una sola vez: si una se protegiera de los ciclos y la otra no, la
 * pantalla andaría y la descarga colgaría el navegador.
 */
export function partesDelCamino(
  categorias: readonly CategoriaPlana[],
  id: string,
): string[] {
  const porId = new Map(categorias.map((c) => [c.id, c]));
  const partes: string[] = [];
  let actual = porId.get(id);
  const vistas = new Set<string>();
  while (actual && !vistas.has(actual.id)) {
    vistas.add(actual.id);
    partes.unshift(actual.nombre);
    actual = actual.parentId ? porId.get(actual.parentId) : undefined;
  }
  return partes;
}

/**
 * Si ya hay una hermana con ese nombre.
 *
 * La base tiene el índice único, pero avisar antes de mandar es la diferencia
 * entre "Ya tenés una categoría así acá" y un error crudo de Postgres.
 */
export function hermanaRepetida(
  categorias: readonly CategoriaPlana[],
  parentId: string | null,
  nombre: string,
  /** Al renombrar, la propia categoría no cuenta como repetida. */
  exceptoId?: string,
): boolean {
  const buscado = nombre.trim().toLocaleLowerCase("es");
  if (buscado === "") return false;
  return categorias.some(
    (c) =>
      c.id !== exceptoId &&
      (c.parentId ?? null) === parentId &&
      c.nombre.trim().toLocaleLowerCase("es") === buscado,
  );
}

/** Compara nombres como los compara la base: sin acentos, sin mayúsculas, sin espacios. */
function mismoNombre(a: string, b: string): boolean {
  const n = (v: string) =>
    v.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLocaleLowerCase("es");
  return n(a) === n(b);
}

/**
 * Dado un camino como ["Remeras", "Oversize"], cuánto de ese camino YA existe y
 * qué falta crear.
 *
 * Es lo que evita duplicar la mitad del árbol cuando la IA propone
 * "Remeras › Oversize" en una tienda que ya tiene "Remeras": se reusa la que
 * está y solo se crea la que falta. Compara igual que el índice único de la base
 * —sin acentos ni mayúsculas— para no crear una "remeras" al lado de "Remeras" y
 * comerse el error de duplicado.
 *
 * `madreId` es la última que existe (o `null` si hay que empezar de la raíz), y
 * `faltan` son los nombres que hay que crear colgando de ahí, en orden.
 */
export function resolverCamino(
  categorias: readonly CategoriaPlana[],
  camino: readonly string[],
): { madreId: string | null; faltan: string[] } {
  const limpio = camino.map((c) => c.trim()).filter((c) => c !== "");
  let madreId: string | null = null;
  let i = 0;

  for (; i < limpio.length; i++) {
    const hija = categorias.find(
      (c) => (c.parentId ?? null) === madreId && mismoNombre(c.nombre, limpio[i]!),
    );
    // La primera que no está corta la búsqueda: de ahí para abajo es todo nuevo.
    if (!hija) break;
    madreId = hija.id;
  }

  return { madreId, faltan: limpio.slice(i) };
}
